import React, { useCallback, useEffect, useMemo } from "react";
import {
  Dimensions,
  ImageBackground,
  ScrollView,
  StatusBar,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  MainButton,
  MainText,
  MainTitle,
  PressableOpacity,
} from "app/components/common";
import {
  acceptPerfectStepRequest,
  selectStep,
} from "@microdoing/shared/services/userCampaign/actions";
import i18n from "@microdoing/shared/i18n";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { widthPercentageToDP as wp } from "react-native-responsive-screen";
import ActionText from "./ActionText";
import selectLanguage from "../../utils/selectLanguage";
import SocialSheet from "app/scenes/CurrentTrackScreen/DailyFocusScreen/SelectAction/SocialSheet";
import isActionEmpty from "@microdoing/shared/utils/isActionEmpty";
import {
  getCampaignSelectedSteps,
  getIsValidateStepDisabled,
  getSelectionRequesting,
  getStepSelected,
  getUserCampaignId,
} from "@microdoing/shared/services/userCampaign/selectors";
import theme from "app/Theme/Theme";
import { getSeeActionAgain } from "@microdoing/shared/services/action/selectors";
import { useNavigation } from "@react-navigation/native";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import selectActionLanguage from "app/utils/selectActionLanguage";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsDailyFocusActionScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { isIos, isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebAnalysisBoxing from "app/components/common/Web/WebAnalysisBoxing";
import CommentList from "../FeedbackScreen/ReflexiveAnalysis/Action/CommentList";
import Reminders from "./Reminders";
import normalize from "app/utils/normalize";
import FullScreenCard from "../CurrentTrackScreen/DailyFocusScreen/SelectAction/FullScreenCard";
import CardInfos from "../CurrentTrackScreen/DailyFocusScreen/SelectAction/CardInfos";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import RoundCrossIcon from "app/components/Icons/RoundCrossIcon";

import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";

let { width, height } = Dimensions.get("window");

const firstImage = require("../../../assets/images/BackgroundGradient.png");

const AnimatedCloseIcon = RoundCrossIcon;

const MODAL_PADDING = "xm";

const ICON_SIZE = normalize(16);

export const MARGIN_HORIZONTAL = wp("8%");

const ActionScreen = ({ route }) => {
  const stepSelected = useSelector(getStepSelected);
  const seeActionAgain = useSelector(getSeeActionAgain);
  const userCampaignId = useSelector(getUserCampaignId);
  const steps = useSelector(getCampaignSelectedSteps);
  const language = useSelector(getLanguage);
  const { title, how, why } = stepSelected?.content || {};
  const contentTitle = selectActionLanguage({ text: title, language });
  const contentHow = selectActionLanguage({ text: how, language });
  const contentWhy = selectActionLanguage({ text: why, language });
  const { category, rating, totalDone, skills } = stepSelected || {};
  const selectionRequesting = useSelector(getSelectionRequesting);

  const insets = useSafeAreaInsets();

  const actionTitle = selectLanguage({ text: stepSelected?.title, language });

  const isDisabled = useSelector(getIsValidateStepDisabled);

  const activeIndex = steps.findIndex(el => !el.done);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const {
    isComingFromAnalysis = true,
    isSocialSheetOpen = false,
    isComingFromSelectAction = false,
    cardIndex = 0,
  } = route?.params ?? {};

  const imageBackground = useMemo(() => {
    try {
      if (cardIndex == "0") {
        return require("../../../assets/images/BackgroundGradient.png");
      } else if (cardIndex == "1") {
        return require("../../../assets/images/BackgroundGradient3.png");
      } else {
        return require("../../../assets/images/BackgroundGradient2.png");
      }
    } catch (e) {
      console.log(e);
    }
  }, [cardIndex]);

  const imageWidth = useSharedValue(width);
  const imageHeight = useSharedValue(height / 2);

  const acceptStep = () => {
    dispatch(
      acceptPerfectStepRequest({
        userCampaignId,
        stepId: stepSelected.step,
      }),
    );
  };

  const validateAction = () => {
    const activeStep = steps[activeIndex];
    dispatch(
      selectStep({
        stepSelected: activeStep,
        stepSelectedIndex: activeIndex,
      }),
    );
    navigation.navigate("ReflexiveAnalysisType");
  };

  const scrollOffset = useSharedValue(0);

  const handleExit = () => navigation.goBack();

  const animatedExitIconStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: scrollOffset.value }],
    };
  }, []);

  const closeIconColor = useDerivedValue(() => {
    return scrollOffset.value >
      imageHeight.value -
        (insets.top ? insets.top : theme.spacing.xm) -
        ICON_SIZE
      ? "rgba(0,0,0,1)"
      : "rgba(255,255,255,1)";
  }, [scrollOffset.value, imageHeight.value, insets.top]);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollOffset.value = event.contentOffset.y;
    },
  });

  const onLayoutCard = useCallback(event => {
    const layout = event.nativeEvent.layout;

    imageHeight.value = layout.height;
  }, []);

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsDailyFocusActionScreen}>
      <WebAnalysisBoxing
        leftComponent={
          <Box
            paddingHorizontal={isWebDesktop ? "l" : "null"}
            paddingVertical={isWebDesktop ? "sl" : "null"}
            borderRadius={8}
            flex={1}
            backgroundColor="neutral.white"
          >
            <StatusBar hidden barStyle="light-content" />

            <Animated.ScrollView
              scrollsToTop
              showsVerticalScrollIndicator={false}
              onScroll={scrollHandler}
              scrollEventThrottle={16}
              style={{
                width: "100%",
              }}
              contentContainerStyle={{
                paddingBottom: isComingFromAnalysis ? 100 : 200,
              }}
            >
              <Box>
                <ImageBackground
                  onLayout={onLayoutCard}
                  source={imageBackground}
                  style={{
                    paddingHorizontal: theme.spacing[MODAL_PADDING],
                    paddingVertical: theme.spacing.xm,
                    paddingTop: insets.top ? insets.top : theme.spacing.xm,
                  }}
                  pointerEvents="none"
                >
                  {!isWebDesktop && (
                    <Animated.View
                      style={[
                        {
                          position: "absolute",
                          top: insets.top ? insets.top : theme.spacing.m,
                          right: 20,
                          zIndex: 30,
                        },
                        animatedExitIconStyle,
                      ]}
                    >
                      <PressableOpacity
                        onPress={handleExit}
                        hitSlop={theme.hitSlop}
                      >
                        <AnimatedCloseIcon
                          height={normalize(32)}
                          width={normalize(32)}
                          color={closeIconColor}
                        />
                      </PressableOpacity>
                    </Animated.View>
                  )}
                  <MainTitle
                    variant="l6-bold"
                    color="neutral.white"
                    marginRight="xxl"
                  >
                    {!!actionTitle ? actionTitle : "Action "}
                  </MainTitle>
                  <CardInfos
                    skill={selectLanguage({
                      text: skills?.[0]?.label,
                      language,
                    })}
                  />
                  <MainTitle
                    variant="l5-bold"
                    color="neutral.white"
                    marginBottom="xs"
                  >
                    {i18n.t("challenge")}
                  </MainTitle>
                  <ActionText
                    textStyle={{ color: "white" }}
                    text={contentTitle ? contentTitle : " "}
                  />
                </ImageBackground>
                <Box paddingHorizontal={MODAL_PADDING}>
                  {!isActionEmpty(contentWhy) && (
                    <>
                      <MainTitle
                        marginTop="m"
                        variant="l4-bold"
                        marginBottom="xxs"
                      >
                        {i18n.t("why")}
                      </MainTitle>
                      <ActionText text={contentWhy} />
                    </>
                  )}

                  {!isActionEmpty(contentHow) && (
                    <>
                      <MainTitle
                        variant="l4-bold"
                        marginTop="sm"
                        marginBottom="xxs"
                      >
                        {i18n.t("concrete-example")}
                      </MainTitle>
                      <ActionText text={contentHow} />
                    </>
                  )}
                </Box>
              </Box>
            </Animated.ScrollView>
            {isWebDesktop && (
              <Box
                width="100%"
                alignSelf="center"
                backgroundColor="neutral.white"
              >
                <MainButton
                  isDisabled={seeActionAgain && isDisabled}
                  marginTop={0}
                  isContentCenter
                  isLoading={selectionRequesting}
                  onPress={seeActionAgain ? validateAction : acceptStep}
                  text={
                    seeActionAgain
                      ? i18n.t("validate-action")
                      : i18n.t("accept-action")
                  }
                />
              </Box>
            )}
            {!isWebDesktop && (
              <SocialSheet
                hasShadow={false}
                contentTitle={contentTitle}
                isSmall={isComingFromAnalysis}
                closedSnapPoint={isDisabled ? normalize(170) : normalize(150)}
                isOpen={isSocialSheetOpen}
                rating={rating}
                totalDone={totalDone}
                numberOfComments={stepSelected?.numberOfComments || 0}
                users={stepSelected?.users || []}
                button={
                  (!isComingFromAnalysis || isComingFromSelectAction) && (
                    <Box
                      width="100%"
                      alignSelf="center"
                      backgroundColor="neutral.white"
                      paddingBottom="s"
                    >
                      <MainButton
                        colors={
                          cardIndex === 0
                            ? [
                                theme.colors["gradient.blueGradient.start"],
                                theme.colors["gradient.blueGradient.end"],
                              ]
                            : cardIndex === 1
                            ? [
                                theme.colors["gradient.greenGradient.start"],
                                theme.colors["gradient.greenGradient.end"],
                              ]
                            : [
                                theme.colors["gradient.orangeGradient.start"],
                                theme.colors["gradient.orangeGradient.end"],
                              ]
                        }
                        isDisabled={seeActionAgain && isDisabled}
                        marginTop={0}
                        marginBottom="xs"
                        isContentCenter
                        isLoading={selectionRequesting}
                        onPress={
                          seeActionAgain && !isComingFromSelectAction
                            ? validateAction
                            : acceptStep
                        }
                        text={
                          seeActionAgain && !isComingFromSelectAction
                            ? i18n.t("validate-action")
                            : i18n.t("accept-action")
                        }
                      />
                      {!!isDisabled && (
                        <MainText
                          textAlign="center"
                          variant="l2-regular"
                          color="neutral.600"
                        >
                          {i18n.t("done-action-blocked-explanation")}
                        </MainText>
                      )}
                    </Box>
                  )
                }
              />
            )}
          </Box>
        }
        rightComponent={
          <Box
            flex={1}
            paddingHorizontal={{ web: "l", smallWeb: "sl" }}
            paddingVertical={{ web: "sl" }}
            overflow="scroll"
          >
            <CommentList />
          </Box>
        }
      />
    </WebDesktopView>
  );
};

export default ActionScreen;
